import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgression } from '../../auth/ProgressionProvider';
import { useSeason } from '../../auth/SeasonProvider';
import { listBuiltinWavesTracks } from '@shared/builtinWavesMaps';
import {
  chartDisplayBpm,
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { VARIANTS, type Variant, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { isTutorialUnseen } from '../../game/tutorialFlag';
import { TrophyStrip } from '../../components/TrophyStrip';
import { ModeSeoSection } from '../ModeSeoSection';
import '../../components/trophyStrip.css';
import '../home.css';
import './wavesHome.css';

const VARIANT_STORAGE_KEY = 'gravitydash:waves-home-variant:v1';

function loadStoredVariant(): Variant {
  try {
    const raw = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (raw && (VARIANTS as ReadonlyArray<string>).includes(raw)) {
      return raw as Variant;
    }
  } catch (err) {
    console.warn('Failed to read Waves variant from localStorage:', err);
  }
  return 'normal';
}

function persistVariant(variant: Variant): void {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch (err) {
    console.warn('Failed to persist Waves variant to localStorage:', err);
  }
}

const VARIANT_DESCRIPTIONS: Record<Variant, string> = {
  normal: 'Wider corridor, fewer obstacles. Find the rhythm before the gates close in.',
  hard: 'Narrower corridor, denser charts. Less room to recover.',
  extreme: 'Tight tunnels, packed spikes, faster scroll. One mistake ends the run.',
};

/**
 * Waves-mode home page. Mirrors {@link
 * import('../guitar/GuitarHomePage.js').GuitarHomePage} structurally
 * — variant selector, track list, tutorial gate — but lists the
 * {@link import('@shared/builtinWavesMaps').BUILTIN_WAVES_TRACKS}
 * catalogue (12 tracks × 3 variants of one-button corridor charts)
 * instead of Guitar's mania notes.
 */
export function WavesHomePage() {
  const { forMap } = useProgression();
  const { crownsForMap } = useSeason();
  const tracks = listBuiltinWavesTracks();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<Variant>(() => loadStoredVariant());

  useEffect(() => {
    saveModePref('waves');
  }, []);

  useEffect(() => {
    if (isTutorialUnseen('waves')) {
      navigate(modePath('waves', 'tutorial'), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== VARIANT_STORAGE_KEY && event.key !== null) {
        return;
      }
      setVariant(loadStoredVariant());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleVariantChange = (next: Variant) => {
    setVariant(next);
    persistVariant(next);
  };

  const featuredTrack = tracks[0];
  const featuredChart = featuredTrack.variants[variant].chart;

  return (
    <div className="home waves-home">
      <header className="home-hero">
        <h1 className="home-hero-kicker">
          BEAT<span style={{ color: 'var(--accent-cyan, #3bd8ff)' }}>WAVES</span>
        </h1>
        <div className="home-cta">
          <Link
            to={modePath('waves', `play/${featuredChart.slug}`)}
            className="btn btn-primary btn-large"
            data-testid="waves-play-cta"
          >
            Play {stripVariantSuffix(featuredChart)}
          </Link>
          <Link
            to={modePath('waves', 'tutorial')}
            className="btn"
            data-testid="waves-tutorial-cta"
          >
            How to play
          </Link>
          <Link to="/calibrate" className="btn btn-ghost">
            Calibrate audio
          </Link>
        </div>
      </header>

      <section className="home-maps">
        <div className="home-maps-header">
          <h2>Tracks</h2>
          <p className="muted faint">
            One-button corridor charts built from the same NCS tracks Circles
            and Guitar use — every obstacle lands on a real onset.
          </p>
        </div>

        <div
          className="variant-selector"
          role="group"
          aria-label="Waves density"
          data-testid="waves-variant-selector"
        >
          {VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={variant === v}
              className={`variant-tab variant-tab-${v} ${variant === v ? 'is-active' : ''}`}
              onClick={() => handleVariantChange(v)}
              data-testid={`waves-variant-tab-${v}`}
            >
              {variantLabel(v)}
            </button>
          ))}
          <p className="variant-hint muted" aria-live="polite">
            {VARIANT_DESCRIPTIONS[variant]}
          </p>
        </div>

        <ol className="map-list">
          {tracks.map((track, idx) => {
            const chart = track.variants[variant].chart;
            const obstacleCount = chart.obstacles.length;
            const trophies = forMap(chart.slug, 'waves');
            return (
              <li key={track.baseSlug} className="map-list-item">
                <Link
                  to={modePath('waves', `play/${chart.slug}`)}
                  className="map-row waves-map-row"
                  data-testid={`waves-map-card-${track.baseSlug}`}
                  style={
                    {
                      '--map-bg': chart.palette.bg,
                      '--map-bg-accent': chart.palette.bgAccent,
                      '--map-pad': chart.palette.pad,
                      '--map-player': chart.palette.player,
                      '--map-hazard': chart.palette.hazard,
                    } as React.CSSProperties
                  }
                >
                  <div className="map-row-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="map-row-preview waves-map-row-preview" aria-hidden="true">
                    <span className="waves-preview-corridor-top" />
                    <span className="waves-preview-trajectory" />
                    <span className="waves-preview-corridor-bottom" />
                  </div>
                  <div className="map-row-body">
                    <div className="map-row-title">{stripVariantSuffix(chart)}</div>
                    <div className="map-row-desc">{chart.description}</div>
                    {chart.mapper && (
                      <div
                        className="map-row-mapper muted faint"
                        data-testid={`waves-map-mapper-${track.baseSlug}`}
                      >
                        Source map: {chart.mapper}
                      </div>
                    )}
                  </div>
                  <div className="map-row-meta">
                    <div className="map-row-bpm">{chartDisplayBpm(chart.bpm).toFixed(0)} BPM</div>
                    <div className="map-row-duration" title="Duration">
                      {formatDuration(chartDurationSeconds(chart.bpm, chart.totalBeats))}
                    </div>
                    <div
                      className="map-row-pad-count"
                      title={`${obstacleCount} obstacles on ${variantLabel(variant)}`}
                    >
                      {obstacleCount} obstacles
                    </div>
                    <div
                      className="map-row-difficulty"
                      aria-label={`Difficulty ${chart.difficulty}`}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < chart.difficulty ? 'pip pip-on' : 'pip'}
                        />
                      ))}
                    </div>
                    <TrophyStrip
                      stars={trophies.stars}
                      cups={trophies.cups}
                      crowns={crownsForMap(chart.slug, 'waves')}
                      size="sm"
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      <ModeSeoSection
        mode="waves"
        playHref={modePath('waves', `play/${featuredChart.slug}`)}
        playLabel={`Play ${stripVariantSuffix(featuredChart)}`}
      />
    </div>
  );
}
